import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'

import { cn } from '../../lib/utils'

interface SelectOption<T extends string> {
  value: T
  label: string
}

interface SelectProps<T extends string> {
  options: readonly SelectOption<T>[]
  value: T
  onValueChange: (value: T) => void
  placeholder?: string
  disabled?: boolean
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  className?: string
  /** Width of the trigger; the popup matches the trigger's width. */
  triggerClassName?: string
}

/* Radix refuses `<Select.Item value="">` — it reserves the empty string for
 * "cleared, show the placeholder". base-ui allows it, and every filter in the
 * design system uses `{ value: '', label: 'All statuses' }` as its no-filter
 * option, with applyTableState reading '' as "not filtering".
 *
 * Rather than rewrite every FilterDef and the table state that reads them, the
 * empty string is swapped for a private sentinel on the way in and back again
 * on the way out. Call sites and applyTableState keep their contract; the
 * substitution lives and dies inside this file. */
const EMPTY_VALUE = '__rh_empty__'
const toRadix = (value: string) => (value === '' ? EMPTY_VALUE : value)
const fromRadix = (value: string) => (value === EMPTY_VALUE ? '' : value)

/**
 * A closed API on purpose: callers pass options and get a select. Swapping the
 * headless library underneath does not touch a single call site — which is what
 * made this file the whole of the base-ui → Radix change for <Select>.
 */
function Select<T extends string>({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  disabled,
  id,
  className,
  triggerClassName,
  ...props
}: SelectProps<T>) {
  return (
    <SelectPrimitive.Root
      value={toRadix(value)}
      onValueChange={(next) => onValueChange(fromRadix(next) as T)}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-label={props['aria-label']}
        aria-invalid={props['aria-invalid']}
        data-slot="select-trigger"
        className={cn(
          'border-border bg-bg-elev text-fg flex h-9 items-center justify-between gap-2 rounded-md border px-3 text-base transition-colors duration-100 ease-out outline-none',
          'hover:bg-bg-subtle',
          'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
          'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          'aria-invalid:border-err',
          className,
          triggerClassName,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon className="text-fg-subtle flex">
          <ChevronDown className="size-3.5" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          data-slot="select-popup"
          className={cn(
            'border-border bg-bg-elev shadow-pop z-50 max-h-72 min-w-(--radix-select-trigger-width) overflow-hidden rounded-lg border p-1 outline-none',
            'origin-(--radix-select-content-transform-origin) duration-100 ease-out',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          <SelectPrimitive.Viewport className="max-h-70 overflow-y-auto overscroll-contain">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={toRadix(option.value)}
                className={cn(
                  'text-fg flex cursor-default items-center justify-between gap-2 rounded-sm px-2.5 py-2 text-base outline-none select-none',
                  'data-[highlighted]:bg-bg-subtle',
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="text-fg-muted flex">
                  <Check className="size-3" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

export { Select, type SelectOption, type SelectProps }
